import { fetchCampaigns } from 'admin/actions/campaign';
import { fetchOrganizations } from 'admin/actions/organization';
import { getPrivateMoney, getPrivateMoneys } from 'admin/actions/private-moneys';
import { fetchShops } from 'admin/actions/shops';
import { getActiveUserTagGroupsWithItems, NormalizedTagGroup } from 'admin/actions/user-tag-group';
import { Button } from 'admin/components/atoms/Buttons';
import DateTimeRange from 'admin/components/atoms/DateTimeRange';
import Label from 'admin/components/atoms/Label';
import MultipleCheckBoxes from 'admin/components/atoms/MultipleCheckBoxes';
import Select from 'admin/components/atoms/Select';
import TextArea from 'admin/components/atoms/TextArea';
import TextBox from 'admin/components/atoms/TextBox';
import TimeRange from 'admin/components/atoms/TimeRange';
import LabeledRadio from 'admin/components/molecules/LabeledRadio';
import { isNullOrUndifined, toQueryParams } from 'admin/utils';
import { toPointExpiresAtFormValue, willRepickChangePointExpiresAt } from 'admin/utils/pointExpiry';
import _ from 'lodash';
import moment, { Moment } from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import styled, { ThemeProvider } from 'styled-components';
import { useAppSelector } from '~/javascripts/admin/hooks/useApp';
import DateTime from '../../atoms/DateTime';
import FormErrorMessage from '../../atoms/FormErrorMessage';
import Grid from '../../atoms/Grid';
import LocalizedMask from '../../atoms/LocalizedMask';
import Required from '../../atoms/Required';
import Tooltip from '../../atoms/Tooltip';
import ApiResults from '../../molecules/ApiResults';
import TagSelectionEditor, { TagSelection } from '../TagSelectionEditor';
import AmountBasedPointRules, { AmountRule } from './AmountBasedPointRules';
import ProductBasedPointRules, { ProductBasedPointRule } from './ProductBasedPointRules';
import SelectedShopsEditor from './SelectedShopsEditor';

type Props = {
  mode: 'create' | 'edit';
  onSubmit: (values: CampaignForm) => void;
  messages?: {
    text: string;
    isError?: boolean;
  }[];
  campaign?: CampaignForm;
  campaignSummary?: {
    point_transfer_count: number;
    received_account_count: number;
    total_point_amount: number;
  };
};

// Names are kept alongside the ids so the confirmation modal can show them without
// reloading the label groups, mirroring how applicableShops carries {id, name}.
export type ShopLabelSelection = {
  // null while a freshly added row has no group picked yet.
  groupId: string | null;
  groupName: string;
  itemIds: string[];
  itemNames: string[];
};

// Kept in form state rather than synthesized at render time, so the row's × can actually
// remove it instead of it being re-added on the next render.
const emptyShopLabelRow = (): ShopLabelSelection => ({
  groupId: null,
  groupName: '',
  itemIds: [],
  itemNames: [],
});

type ShopLabelResponse = NonNullable<CampaignResponse['shop_labels']>[number];

export const toShopLabelSelections = (
  shopLabels: ShopLabelResponse[] | null | undefined
): ShopLabelSelection[] =>
  (shopLabels ?? []).reduce<ShopLabelSelection[]>((rows, label) => {
    const group = label.user_tag_group;
    const row = rows.find((r) => r.groupId === group.id);
    const name = label.name_ja || label.name_en;
    if (row) {
      row.itemIds.push(label.id);
      row.itemNames.push(name);
      return rows;
    }
    return rows.concat([
      {
        groupId: group.id,
        groupName: group.name_ja || group.name_en,
        itemIds: [label.id],
        itemNames: [name],
      },
    ]);
  }, []);

const toShopLabelSelectionsFromRows = (
  groups: NormalizedTagGroup[],
  rows: TagSelection[]
): ShopLabelSelection[] =>
  rows.map((row) => {
    const group = groups.find((g) => g.id === row.groupId);
    return {
      groupId: row.groupId,
      groupName: group?.name ?? '',
      itemIds: row.itemIds,
      itemNames: row.itemIds.map((id) => group?.items.find((item) => item.id === id)?.name ?? ''),
    };
  });

// The API returns labels that have since been deactivated so past selections stay editable,
// but getActiveUserTagGroupsWithItems only returns active ones. Without merging them back in,
// a deactivated label would vanish from the form and be dropped on save.
const mergeSelectionsIntoGroups = (
  groups: NormalizedTagGroup[],
  selections: ShopLabelSelection[]
): NormalizedTagGroup[] => {
  const merged = groups.map((group) => ({ ...group, items: [...group.items] }));
  selections.forEach((row) => {
    const { groupId } = row;
    if (!groupId) return;
    let group = merged.find((g) => g.id === groupId);
    if (!group) {
      group = { id: groupId, name: row.groupName, maxSelections: 0, items: [], subgroups: [] };
      merged.push(group);
    }
    const { items } = group;
    row.itemIds.forEach((id, index) => {
      if (items.some((item) => item.id === id)) return;
      items.push({
        id,
        name: row.itemNames[index] ?? '',
        subgroupId: null,
        subgroupName: null,
      });
    });
  });
  return merged;
};

export const toShopSpecification = (c: {
  shops?: unknown[] | null;
  blacklisted_shops?: unknown[] | null;
  shop_labels?: unknown[] | null;
}): CampaignForm['shopSpecification'] => {
  if ((c.shops ?? []).length > 0) return 'whitelist';
  if ((c.blacklisted_shops ?? []).length > 0) return 'blacklist';
  if ((c.shop_labels ?? []).length > 0) return 'labels';
  return 'none';
};

export type CampaignForm = {
  id?: string | null;
  privateMoney: {
    id: string;
    name: string;
    topup_methods?: string[];
    organization?: { code: string; name: string };
  };
  organization: { code: string; name: string } | null;
  pointBearerShop: { id: string; name: string } | null;
  event: 'payment' | 'topup' | 'external-transaction' | null;
  name: string;
  description: string;
  priority: number | null;
  isExclusive: boolean;
  dateRange: {
    from: Moment | null;
    to: Moment | null;
  } | null;
  residentCheck: 'none' | 'required' | 'optional';
  shopSpecification: 'none' | 'whitelist' | 'labels' | 'blacklist';
  applicableShops: { id: string; name: string }[];
  applicableShopLabels: ShopLabelSelection[];
  daysOfWeek: number[];
  timeRanges: { from: string; to: string }[];
  amountBasedPointRules: {
    uniformAmountRule: AmountRule;
    customAmountRules: AmountRule[];
  };
  productBasedPointRules: {
    miniumNumberForCombinationPurchase: number;
    productBasedPointRules: ProductBasedPointRule[];
  };
  selectedPointRulesType: (typeof pointRulesTypes)[number]['value'];
  budgetCapsAmount: number | null;
  maxTotalPointAmount: number | null;
  maxPointAmount: number | null;
  pointExpiresAt: Moment | null;
  pointExpiresInDays: number | null;
  applicableTransactionMetadata: string | null;
};

type CampaignResponse = Awaited<ReturnType<typeof fetchCampaigns>>['rows'][0];

const DEFAULT_CAMPAGIN_VALUES: Partial<CampaignForm> = {
  id: null,
  organization: null,
  pointBearerShop: null,
  event: null,
  name: '',
  description: '',
  priority: null,
  isExclusive: false,
  dateRange: null,
  residentCheck: 'none',
  shopSpecification: 'none',
  applicableShops: [],
  applicableShopLabels: [],
  daysOfWeek: [],
  timeRanges: [],
  amountBasedPointRules: {
    uniformAmountRule: {
      amount: null,
      unit: 'percent',
      from: null,
      to: null,
    },
    customAmountRules: [
      {
        amount: null,
        unit: 'percent',
        from: null,
        to: null,
      },
    ],
  },
  productBasedPointRules: {
    miniumNumberForCombinationPurchase: 0,
    productBasedPointRules: [],
  },
  selectedPointRulesType: 'allAmountUniform',
  budgetCapsAmount: null,
  maxTotalPointAmount: null,
  maxPointAmount: null,
  pointExpiresAt: null,
  pointExpiresInDays: null,
};

const pageTheme = {
  atoms: {
    label: {
      marginBottom: '0',
      padding: '4px 0',
    },
  },
};

const FormGrid = styled(Grid.Container)`
  margin: 16px 0;

  ${Select as any} {
    line-height: 1.5;
  }

  ${DateTime} {
    margin-bottom: 0;
  }
  ${DateTimeRange} {
    margin-bottom: 0;
    ${DateTime} {
      flex: 1;
    }
  }
`;

const LabelWithTooltipWrapper = styled.div`
  display: flex;
  gap: 2px;
  align-items: center;
  margin-top: 0.5rem;
`;

const ErrorMessage = styled.div`
  color: red;
`;

const PointRulesSelect = styled(Select)`
  max-width: 400px;
`;

const TagGroupsLoadErrorNote = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  color: #999;
`;

const RadioWrapper = styled.div`
  display: flex;
  gap: 16px;
`;

const weekTypes = [
  { label: '月', value: 0 },
  { label: '火', value: 1 },
  { label: '水', value: 2 },
  { label: '木', value: 3 },
  { label: '金', value: 4 },
  { label: '土', value: 5 },
  { label: '日', value: 6 },
];

const campaignTypes = [
  { label: '支払い', value: 'payment' },
  { label: 'チャージ', value: 'topup' },
  { label: 'ポケペイ外決済', value: 'external-transaction' },
] as const;

const subjectType = [
  { label: '取引総額', value: 'all' },
  { label: 'マネーのみ', value: 'money' },
];

const chargingMethods = [
  { label: 'クレジットカード', value: 'creditcard' },
  { label: '銀行振込', value: 'bankpay' },
  { label: 'セブン銀行ATM', value: 'sevenbank-atm' },
];

const pointRulesTypes = [
  {
    label: '取引総額',
    value: 'allAmountUniform',
  },
  {
    label: '取引総額（金額レンジごと）',
    value: 'allAmountCustom',
  },
  {
    label: '取引マネー金額',
    value: 'moneyAmountUniform',
  },
  {
    label: '取引マネー金額（金額レンジごと）',
    value: 'moneyAmountCustom',
  },
  {
    label: '商品ごと',
    value: 'product',
  },
] as const;

const getAvailablePointRulesType = (campaignType: (typeof campaignTypes)[number]['value']) => {
  if (campaignType === 'topup') {
    return ['allAmountUniform', 'allAmountCustom', 'moneyAmountUniform', 'moneyAmountCustom'];
  }
  if (campaignType === 'external-transaction') {
    return ['allAmountUniform', 'allAmountCustom', 'product'];
  }

  return pointRulesTypes.map((v) => v.value);
};

const toOption = <T extends { name: string }>(item: T) => ({
  label: item.name,
  value: item,
});

const noPreviousCampaignOption = {
  label: 'なし',
  value: null,
};

const isBool = (v) => {
  if (_.isBoolean(v)) return v;
  if (v === 'TRUE') return true;
  return false;
};

const EditCampaign = ({ mode, onSubmit, campaign, messages, campaignSummary }: Props) => {
  const user = useAppSelector((state) => state.user);
  const defaultMoney = user.defaultMoney();
  const [privateMoneys, setPrivateMoneys] = useState<{ id: string; name: string }[]>([]);
  const [organizations, setOrganizations] = useState<{ code: string; name: string }[]>([]);
  const [shops, setShops] = useState<{ id: string; name: string }[]>([]);
  const [inputErrMessage, setInputErrMessage] = useState<string | null>(null);

  const [privateMoneyTopupMethods, setPrivateMoneyTopupMethods] = useState<any[]>([]);

  const [previousCampaigns, setPreviousCampaigns] = useState<
    {
      label: string;
      value: CampaignResponse | null;
    }[]
  >([]);
  const [selectedPreviousCampaign, setSelectedPreviousCampaign] = useState<{
    label: string;
    value: CampaignResponse | null;
  }>(noPreviousCampaignOption);
  const [availableShops, setAvailableShops] = useState<
    {
      id: string;
      name: string;
    }[]
  >([]);
  const [availableTagGroups, setAvailableTagGroups] = useState<NormalizedTagGroup[]>([]);
  const [tagGroupsLoading, setTagGroupsLoading] = useState(false);
  const [tagGroupsLoadFailed, setTagGroupsLoadFailed] = useState(false);

  const {
    watch,
    register,
    control,
    setValue,
    reset,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<CampaignForm>({
    defaultValues: {
      privateMoney: defaultMoney,
      ...DEFAULT_CAMPAGIN_VALUES,
    },
  });

  const selectedMoney = watch('privateMoney');
  const selectedMoneyIdRef = useRef<string | null>(null);
  const editableTagGroups = mergeSelectionsIntoGroups(
    availableTagGroups,
    watch('applicableShopLabels') ?? []
  );

  // The API rejects a campaign that carries more than one kind of shop restriction, so the
  // fields belonging to the other choices have to be emptied when switching.
  const selectShopSpecification = (
    onChange: (next: CampaignForm['shopSpecification']) => void,
    next: CampaignForm['shopSpecification']
  ) => {
    onChange(next);
    setAvailableShops([]);
    setValue('applicableShops', []);
    if (next !== 'labels') {
      setValue('applicableShopLabels', []);
    } else if (getValues('applicableShopLabels').length === 0) {
      setValue('applicableShopLabels', [emptyShopLabelRow()]);
    }
  };

  useEffect(() => {
    reset(
      campaign ?? {
        privateMoney: defaultMoney,
        ...DEFAULT_CAMPAGIN_VALUES,
      }
    );
    setAvailableShops(campaign?.applicableShops ?? []);
  }, [campaign, reset]);

  useEffect(() => {
    const requestedMoneyId = selectedMoney.id;
    selectedMoneyIdRef.current = requestedMoneyId;
    setTagGroupsLoadFailed(false);
    if (selectedMoney.id) {
      fetchCampaigns(
        toQueryParams({
          private_money_id: selectedMoney.id,
          per_page: 10000,
        })
      ).then(({ rows }) =>
        setPreviousCampaigns([
          noPreviousCampaignOption,
          ...rows.map((c) => ({ label: c.name, value: c })),
        ])
      );
      // Ignore a response for a money the user has already switched away from.
      const isStale = () => requestedMoneyId !== selectedMoneyIdRef.current;
      const failTagGroups = (err: unknown) => {
        // eslint-disable-next-line no-console
        console.error('Failed to load user tag groups', err);
        if (isStale()) return;
        setAvailableTagGroups([]);
        setTagGroupsLoading(false);
        setTagGroupsLoadFailed(true);
      };
      setTagGroupsLoading(true);
      getPrivateMoney(selectedMoney.id)
        .then((json) => {
          setPrivateMoneyTopupMethods(json.topup_methods ?? []);
          const issuerOrgCode = json.organization?.code;
          // Without the issuer org we cannot even ask for labels, so this is a failure too:
          // reporting "no labels" here would leave the previous money's groups on screen.
          if (!issuerOrgCode) {
            failTagGroups(new Error('private money has no organization code'));
            return undefined;
          }
          return getActiveUserTagGroupsWithItems(issuerOrgCode).then((groups) => {
            if (isStale()) return;
            setAvailableTagGroups(groups);
            setTagGroupsLoading(false);
          });
        })
        .catch(failTagGroups);
    }
  }, [selectedMoney.id]);

  useEffect(() => {
    if (user.isServiceUser()) {
      getPrivateMoneys('per_page=2000').then((moneys) => {
        setPrivateMoneys(moneys.rows as { id: string; name: string }[]);
      });
    } else {
      setPrivateMoneys(user.issuedMoneys() as { id: string; name: string }[]);
    }
    fetchOrganizations(`private_money_id=${defaultMoney?.id}&per_page=10000`).then((orgs) => {
      setOrganizations(orgs.rows as { code: string; name: string }[]);
    });
  }, []);

  const clearFields = ({ clearOrganizations } = { clearOrganizations: true }) => {
    reset({
      privateMoney: getValues('privateMoney'),
      ...DEFAULT_CAMPAGIN_VALUES,
      ...(!clearOrganizations && { organization: getValues('organization') }),
    });
    setInputErrMessage(null);
    setAvailableShops([]);
    if (clearOrganizations) {
      setOrganizations([]);
    }
  };

  const onSelectPreviousCampaign = (op: { label: string; value: CampaignResponse }) => {
    setSelectedPreviousCampaign(op);
    if (op.value === null) {
      clearFields({ clearOrganizations: false });
      return;
    }

    const {
      value: { point_calculation_rule_object, ...props },
    } = op;
    const pointCalculationRule = JSON.parse(point_calculation_rule_object) as any;
    const c = { ...props };
    setValue('name', c.name);
    setValue('description', c.description);
    setValue('dateRange', { from: moment(c.starts_at), to: moment(c.ends_at) });
    setValue('organization', organizations.find((o) => o.code === c.organization.code) || null);

    fetchShops(
      `organization_code=${c.organization.code}&private_money_id=${c.private_money.id}`
    ).then((res: { id: string; name: string }[]) => {
      setShops(res);
      setValue('pointBearerShop', res.find((s) => s.id === c.bear_point_shop_uuid) || null);
    });

    setValue('isExclusive', c.is_exclusive);
    setValue('priority', c.priority);
    setValue('pointExpiresAt', toPointExpiresAtFormValue(c.point_expires_at));
    setValue('pointExpiresInDays', c.point_expires_in_days);
    setValue('event', pointCalculationRule.event);
    setValue(
      'applicableTransactionMetadata',
      pointCalculationRule.applicable_transaction_metadata?.value ?? null
    );
    setValue('daysOfWeek', pointCalculationRule.applicable_days_of_week || []);
    setValue('timeRanges', pointCalculationRule.applicable_time_ranges || []);
    setValue(
      'productBasedPointRules.miniumNumberForCombinationPurchase',
      pointCalculationRule.minimum_number_for_combination_purchase
    );

    setAvailableShops(c.shops || c.blacklisted_shops || []);
    setValue('applicableShopLabels', toShopLabelSelections(c.shop_labels));

    setValue('shopSpecification', toShopSpecification(c));

    const getResidentCheck = (metaData?: {
      key?: string;
      value?: string;
      test?: string;
    }): CampaignForm['residentCheck'] => {
      if (metaData?.key !== 'is_resident' || metaData?.value !== 'true') {
        return 'none';
      }

      return metaData?.test === 'not-equal' ? 'optional' : 'required';
    };

    setValue('residentCheck', getResidentCheck(pointCalculationRule?.applicable_account_metadata));

    const subject = pointCalculationRule.subject || subjectType[0].value;

    if (pointCalculationRule.amount_based_point_rules) {
      const amountBasedPointRules = pointCalculationRule.amount_based_point_rules;
      const rule = amountBasedPointRules[0];
      if (
        amountBasedPointRules.length > 1 ||
        rule.subject_more_than_or_equal ||
        rule.subject_less_than_or_equal
      ) {
        setValue(
          'selectedPointRulesType',
          subject === 'all' ? 'allAmountCustom' : 'moneyAmountCustom'
        );
        setValue(
          'amountBasedPointRules.customAmountRules',
          amountBasedPointRules.map((r: any) => ({
            amount: r.point_amount,
            unit: r.point_amount_unit,
            from: r.subject_more_than_or_equal,
            to: r.subject_less_than_or_equal,
          }))
        );
      } else {
        setValue(
          'selectedPointRulesType',
          subject === 'all' ? 'allAmountUniform' : 'moneyAmountUniform'
        );
        setValue('amountBasedPointRules.uniformAmountRule.amount', rule.point_amount);
        setValue('amountBasedPointRules.uniformAmountRule.unit', rule.point_amount_unit);
      }
      setValue('productBasedPointRules.productBasedPointRules', []);
    }

    if (pointCalculationRule.product_based_point_rules) {
      setValue('selectedPointRulesType', 'product');

      setValue(
        'productBasedPointRules.productBasedPointRules',
        pointCalculationRule.product_based_point_rules.map((v) => ({
          ...v,
          is_multiply_by_count: isBool(v.is_multiply_by_count),
        }))
      );

      setValue('amountBasedPointRules.uniformAmountRule.amount', null);
      setValue('amountBasedPointRules.uniformAmountRule.unit', 'percent');
      setValue('amountBasedPointRules.customAmountRules', [
        {
          amount: null,
          unit: 'percent',
          from: null,
          to: null,
        },
      ]);
    }
  };

  const registerCampaign = (formData: CampaignForm) => {
    setInputErrMessage(null);

    onSubmit(formData);
  };

  const disabled = user.role !== 'SERVICE-MANAGER' && mode === 'edit';

  const creditCardTopupMethod = privateMoneyTopupMethods.find(
    (method) => method.type === 'credit-card'
  );
  const bankPayTopupMethod = privateMoneyTopupMethods.find(
    (method) => method.type === 'paytree-bank'
  );
  const sevenBankTopupMethod = privateMoneyTopupMethods.find(
    (method) => method.type === 'sevenbank-atm'
  );

  const applicableChargingMethods = chargingMethods.filter((method) => {
    if (watch('event') !== 'topup') return false;

    if (method.value === 'creditcard') {
      return !!creditCardTopupMethod;
    }
    if (method.value === 'bankpay') {
      return !!bankPayTopupMethod;
    }
    if (method.value === 'sevenbank-atm') {
      return !!sevenBankTopupMethod;
    }
    return false;
  });

  return (
    <ThemeProvider theme={pageTheme}>
      <form onSubmit={handleSubmit(registerCampaign)}>
        <FormGrid>
          {mode === 'create' && (
            <>
              <Grid.Item size={2} alignCenter>
                <Label>
                  マネー <Required />
                </Label>
              </Grid.Item>
              <Grid.Item size={6}>
                <Controller
                  control={control}
                  name="privateMoney"
                  render={({ field: { value, onChange } }) => (
                    <Select
                      fullWidth
                      isMulti={false}
                      options={privateMoneys.map(toOption)}
                      value={toOption(value)}
                      onChange={(v) => {
                        if (!v) return;
                        onChange(v.value);
                        setSelectedPreviousCampaign(noPreviousCampaignOption);
                        fetchOrganizations(`private_money_id=${v.value.id}&per_page=10000`).then(
                          (orgs) => {
                            setOrganizations(orgs.rows as { code: string; name: string }[]);
                          }
                        );
                        clearFields();
                      }}
                      placeholder="マネーを選択してください。"
                    />
                  )}
                />
              </Grid.Item>

              <Grid.Spacer />

              <Grid.Item size={2} alignCenter>
                <Label>
                  ポイント負担先店舗 <Required />
                </Label>
              </Grid.Item>
              <Grid.Item size={6} container gridSize={2} columnGap={24}>
                <Grid.Item>
                  <Controller
                    control={control}
                    name="organization"
                    render={({ field: { value, onChange } }) => (
                      <Select
                        fullWidth
                        isMulti={false}
                        options={organizations.map(toOption)}
                        value={value ? toOption(value) : null}
                        onChange={(v) => {
                          if (!v) return;
                          onChange(v.value);
                          setShops([]);
                          setValue('pointBearerShop', null);
                          fetchShops(
                            `organization_code=${v.value.code}&private_money_id=${selectedMoney.id}`
                          ).then((s) => {
                            setShops(s as { id: string; name: string }[]);
                          });
                        }}
                        placeholder="「組織」を選択"
                      />
                    )}
                  />
                </Grid.Item>
                <Grid.Item>
                  <Controller
                    control={control}
                    name="pointBearerShop"
                    rules={{ required: '負担先店舗のウォレットを指定して下さい' }}
                    render={({ field: { value, onChange } }) => (
                      <Select
                        fullWidth
                        isMulti={false}
                        options={shops.map(toOption)}
                        value={value ? toOption(value) : null}
                        onChange={(v) => v && onChange(v.value)}
                        placeholder="「店舗」を選択"
                      />
                    )}
                  />
                </Grid.Item>
              </Grid.Item>
              <Grid.Item size={6} startColumn={3}>
                <FormErrorMessage name="pointBearerShop" errors={errors} />
              </Grid.Item>

              <Grid.Spacer />

              <Grid.Item size={2} alignCenter>
                <Label>登録済キャンペーン名</Label>
              </Grid.Item>
              <Grid.Item size={6}>
                <Select
                  fullWidth
                  options={previousCampaigns}
                  value={selectedPreviousCampaign}
                  onChange={onSelectPreviousCampaign}
                  placeholder="登録済キャンペーンを選択してください。"
                />
              </Grid.Item>
            </>
          )}

          {mode === 'edit' && (
            <>
              <Grid.Item size={2} alignCenter nextRow>
                <Label>キャンペーンフルID</Label>
              </Grid.Item>
              <Grid.Item size={6} alignCenter>
                {watch('id')}
              </Grid.Item>

              <Grid.Divider />

              <Grid.Item size={2} alignCenter nextRow>
                <Label>取引件数</Label>
              </Grid.Item>
              <Grid.Item size={6} alignCenter>
                {campaignSummary?.point_transfer_count}
              </Grid.Item>

              <Grid.Divider />

              <Grid.Item size={2} alignCenter nextRow>
                <Label>付与人数</Label>
              </Grid.Item>
              <Grid.Item size={6} alignCenter>
                {campaignSummary?.received_account_count}
              </Grid.Item>

              <Grid.Divider />

              <Grid.Item size={2} alignCenter nextRow>
                <Label>ポイント付与総額</Label>
              </Grid.Item>
              <Grid.Item size={6} alignCenter>
                {campaignSummary?.total_point_amount}
              </Grid.Item>

              <Grid.Divider />

              <Grid.Item size={2} alignCenter nextRow>
                <Label>ポイント負担先店舗</Label>
              </Grid.Item>
              <Grid.Item size={6} alignCenter style={{ wordBreak: 'break-word' }}>
                <Link to={`/shops/${watch('pointBearerShop')?.id}`}>
                  {watch('pointBearerShop')?.name}
                </Link>
              </Grid.Item>
            </>
          )}

          <Grid.Divider />

          <Grid.Item size={2} alignCenter>
            <Label>
              対象取引 <Required />
            </Label>
          </Grid.Item>
          <Grid.Item size={6} container gridSize={2} columnGap={24}>
            <Grid.Item>
              <Controller
                control={control}
                name="event"
                rules={{ required: 'キャンペーン種別を指定して下さい' }}
                render={({ field: { value, onChange } }) => (
                  <Select
                    fullWidth
                    isMulti={false}
                    options={campaignTypes}
                    value={campaignTypes.find((v) => v.value === value) || null}
                    onChange={(v) => {
                      if (!v) return;
                      onChange(v.value);
                      if (v.value === 'topup') {
                        setValue('selectedPointRulesType', 'allAmountUniform');
                      }
                      setValue('applicableTransactionMetadata', null);
                    }}
                    placeholder="「取引」を選択"
                    isDisabled={disabled}
                  />
                )}
              />
            </Grid.Item>

            <Grid.Item>
              <Controller
                control={control}
                name="applicableTransactionMetadata"
                render={({ field: { value, onChange } }) => (
                  <Select
                    fullWidth
                    isMulti={false}
                    options={applicableChargingMethods}
                    value={applicableChargingMethods.find((v) => v.value === value) || null}
                    onChange={(v) => {
                      if (!v) return;
                      onChange(v.value);
                    }}
                    placeholder="「取引条件」を選択"
                    isDisabled={disabled}
                  />
                )}
              />
            </Grid.Item>
            <Grid.Item size={2}>
              <FormErrorMessage name="event" errors={errors} />
            </Grid.Item>
          </Grid.Item>

          <Grid.Divider />

          <Grid.Item size={2}>
            <Label>
              キャンペーン名 <Required />
              <Tooltip id="campaign-name" text="お客様の利用履歴に表示します" />
            </Label>
          </Grid.Item>
          <Grid.Item size={6}>
            <TextArea
              fullWidth
              {...register('name', { required: 'キャンペーン名を入力してください' })}
            />
          </Grid.Item>
          <Grid.Item size={6} startColumn={3}>
            <FormErrorMessage name="name" errors={errors} />
          </Grid.Item>

          <Grid.Spacer />

          <Grid.Item size={2}>
            <Label>
              説明
              <Tooltip id="campaign-description" text="最大200文字までです" />
            </Label>
          </Grid.Item>
          <Grid.Item size={6}>
            <TextArea fullWidth {...register('description')} />
          </Grid.Item>

          <Grid.Divider />

          <Grid.Item size={2}>
            <Label>順序付け</Label>
          </Grid.Item>
          <Grid.Item size={6} container gridSize={2} columnGap={24}>
            <Grid.Item flexDirection="column">
              <Label>
                優先度 <Required />
              </Label>
              <Controller
                control={control}
                name="priority"
                rules={{
                  required: '優先度の数字を入力してください',
                }}
                render={({ field: { value, onChange } }) => (
                  <TextBox
                    fullWidth
                    syncValue={value?.toString() ?? ''}
                    onChange={(e) => /^[0-9]*$/.test(e.target.value) && onChange(e)}
                  />
                )}
              />
            </Grid.Item>
            <Grid.Item flexDirection="column">
              <Label>重ね掛け</Label>
              <Controller
                control={control}
                name="isExclusive"
                render={({ field: { value, onChange } }) => (
                  <Select
                    fullWidth
                    isMulti={false}
                    options={[
                      { label: '有効', value: false },
                      { label: '無効', value: true },
                    ]}
                    value={{ label: value ? '無効' : '有効', value }}
                    onChange={(v) => v !== null && onChange(v.value)}
                    isDisabled={disabled}
                  />
                )}
              />
            </Grid.Item>
          </Grid.Item>
          <Grid.Item size={6} startColumn={3}>
            <FormErrorMessage name="priority" errors={errors} />
          </Grid.Item>

          <Grid.Divider />

          <Grid.Item size={2}>
            <Label>
              対象期間 <Required />
            </Label>
          </Grid.Item>
          <Grid.Item size={6} flexDirection="column">
            <Controller
              control={control}
              name="dateRange"
              rules={{
                validate: (v) => {
                  if (!v?.from || !v?.to) {
                    return 'キャンペーン期間を指定して下さい';
                  }
                  if (v.from > v.to) {
                    return 'キャンペーン期間の指定が正しくありません';
                  }
                  return true;
                },
              }}
              render={({ field: { value, onChange } }) => (
                <DateTimeRange
                  dateRange={{
                    from: value?.from ?? null,
                    to: value?.to ?? null,
                  }}
                  setDateRange={(date) =>
                    onChange({
                      from: date.from ? moment(date.from) : null,
                      to: date.to ? moment(date.to) : null,
                    })
                  }
                  dateTimeProps={{
                    toProps: {
                      timeFormat: 'HH:mm',
                      inputProps: { readOnly: true },
                      showIcon: true,
                      wrapperStyles: { flex: 1 },
                    },
                    fromProps: {
                      timeFormat: 'HH:mm',
                      inputProps: { readOnly: true },
                      showIcon: true,
                      wrapperStyles: { flex: 1 },
                    },
                  }}
                  seperatorProps={{
                    styles: { width: '16px', textAlign: 'center' },
                  }}
                />
              )}
            />
            <FormErrorMessage name="dateRange" errors={errors} />
          </Grid.Item>

          <Grid.Divider />

          <Grid.Item size={2}>
            <LabelWithTooltipWrapper>
              <Label>対象会員</Label>
              <Tooltip
                id="resident-check"
                text={
                  <span>
                    住民認証とはマイナンバーカード認証の住所から
                    <br />
                    例えば設定した市区町村までが一致した状態を指します
                  </span>
                }
              />
            </LabelWithTooltipWrapper>
          </Grid.Item>
          <Grid.Item size={6} flexDirection="column">
            <RadioWrapper>
              <Controller
                control={control}
                name="residentCheck"
                render={({ field: { value, onChange } }) => (
                  <>
                    <LabeledRadio
                      labelText="全員"
                      checked={value === 'none'}
                      setChecked={() => onChange('none')}
                      labelPosition="right"
                    />
                    <LabeledRadio
                      labelText="住民認証あり"
                      checked={value === 'required'}
                      setChecked={() => onChange('required')}
                      labelPosition="right"
                    />
                    <LabeledRadio
                      labelText="住民認証なし"
                      checked={value === 'optional'}
                      setChecked={() => onChange('optional')}
                      labelPosition="right"
                    />
                  </>
                )}
              />
            </RadioWrapper>
          </Grid.Item>

          <Grid.Divider />

          <Grid.Item size={2}>
            <Label>対象店舗</Label>
          </Grid.Item>
          <Grid.Item size={6} flexDirection="column">
            <RadioWrapper>
              <Controller
                control={control}
                name="shopSpecification"
                render={({ field: { value, onChange } }) => (
                  <>
                    <LabeledRadio
                      labelText="指定なし"
                      checked={value === 'none'}
                      setChecked={() => selectShopSpecification(onChange, 'none')}
                      labelPosition="right"
                    />
                    <LabeledRadio
                      labelText="許可リスト"
                      checked={value === 'whitelist'}
                      setChecked={() => selectShopSpecification(onChange, 'whitelist')}
                      labelPosition="right"
                    />
                    <LabeledRadio
                      labelText="許可リスト(店舗ラベル)"
                      checked={value === 'labels'}
                      setChecked={() => selectShopSpecification(onChange, 'labels')}
                      labelPosition="right"
                    />
                    <LabeledRadio
                      labelText="除外リスト"
                      checked={value === 'blacklist'}
                      setChecked={() => selectShopSpecification(onChange, 'blacklist')}
                      labelPosition="right"
                    />
                  </>
                )}
              />
            </RadioWrapper>

            {['whitelist', 'blacklist'].includes(watch('shopSpecification')) && (
              <>
                <Controller
                  control={control}
                  name="applicableShops"
                  rules={{
                    validate: (value) => {
                      if (!['whitelist', 'blacklist'].includes(watch('shopSpecification'))) return;
                      if (value.length === 0) return '店舗を選択してください';
                      return true;
                    },
                  }}
                  render={({ field: { onChange } }) => (
                    <SelectedShopsEditor
                      available_shops={availableShops}
                      designatedMoney={selectedMoney}
                      onChangeShops={onChange}
                    />
                  )}
                />
                <FormErrorMessage name="applicableShops" errors={errors} />
              </>
            )}

            {watch('shopSpecification') === 'labels' && (
              <Controller
                control={control}
                name="applicableShopLabels"
                rules={{
                  // Still enforced when the groups failed to load: letting an empty selection
                  // through would silently register a campaign the API treats as unrestricted.
                  validate: (value) => {
                    if (watch('shopSpecification') !== 'labels') return true;
                    if (value.some((row) => row.itemIds.length === 0)) return '条件選択がありません';
                    return true;
                  },
                }}
                render={({ field: { value, onChange } }) => {
                  if (tagGroupsLoading) return <div>読み込み中...</div>;
                  if (tagGroupsLoadFailed) {
                    return (
                      <TagGroupsLoadErrorNote>
                        店舗ラベルの取得に失敗しました。ページを再読み込みしてください。
                        {value.some((row) => row.itemIds.length > 0) &&
                          '（このまま更新しても設定済みのラベルは保持されます）'}
                      </TagGroupsLoadErrorNote>
                    );
                  }
                  return (
                    <TagSelectionEditor
                      groups={editableTagGroups}
                      value={value.map(({ groupId, itemIds }) => ({ groupId, itemIds }))}
                      onChange={(next) =>
                        onChange(toShopLabelSelectionsFromRows(editableTagGroups, next))
                      }
                      errorMessage={
                        <FormErrorMessage name="applicableShopLabels" errors={errors} />
                      }
                      groupPlaceholder="「グループ」を選択"
                      itemPlaceholder="「ラベル」を選択"
                      // max_selections caps how many labels one shop may carry; the campaign
                      // API ignores it, and OR-within-a-group needs several from one group.
                      forceMulti
                    />
                  );
                }}
              />
            )}
          </Grid.Item>

          <Grid.Divider />

          <Grid.Item size={2} alignCenter>
            <Label>対象曜日</Label>
          </Grid.Item>
          <Grid.Item size={6}>
            <Controller
              control={control}
              name="daysOfWeek"
              render={({ field: { value, onChange } }) => (
                <MultipleCheckBoxes
                  options={weekTypes}
                  value={value}
                  onChange={onChange}
                  disabled={disabled}
                />
              )}
            />
          </Grid.Item>

          <Grid.Spacer />

          <Grid.Item size={2}>
            <Label>時間帯</Label>
          </Grid.Item>
          <Grid.Item size={6} flexDirection="column">
            <Controller
              control={control}
              name="timeRanges"
              render={({ field: { value, onChange } }) => (
                <>
                  <div>
                    <Button
                      type="button"
                      onClick={(e) => {
                        onChange([
                          ...value,
                          {
                            from: '00:00',
                            to: '12:00',
                          },
                        ]);
                      }}
                      disabled={disabled}
                    >
                      時間帯を追加する
                    </Button>
                  </div>
                  {value.map((tr, index) => (
                    <TimeRange
                      key={`timerange${index}`}
                      value={tr}
                      onChange={(v) => {
                        onChange(
                          value.map((t, i) => {
                            if (i === index) {
                              return v;
                            }
                            return t;
                          })
                        );
                      }}
                      onRemove={() => {
                        const timeranges = [...value];
                        timeranges.splice(index, 1);
                        onChange(timeranges);
                      }}
                      disabled={disabled}
                    />
                  ))}
                </>
              )}
            />
          </Grid.Item>

          <Grid.Divider />

          <Grid.Item size={2}>
            <Label>
              ポイント還元
              <Required />
            </Label>
          </Grid.Item>
          <Grid.Item size={6} flexDirection="column">
            {!watch('event') && <Label>対象取引を選択後、設定欄を表示します</Label>}

            {!!watch('event') && (
              <Grid.Item size={12} flexDirection="column">
                <Label>還元対象</Label>
                <Controller
                  control={control}
                  name="selectedPointRulesType"
                  render={({ field: { value, onChange } }) => (
                    <PointRulesSelect
                      isMulti={false}
                      options={pointRulesTypes.filter((v) =>
                        getAvailablePointRulesType(watch('event') ?? 'payment').includes(v.value)
                      )}
                      value={pointRulesTypes.find((v) => v.value === value) || null}
                      onChange={(v: (typeof pointRulesTypes)[number]) => {
                        if (!v) return;
                        onChange(v.value);
                      }}
                      isDisabled={disabled}
                    />
                  )}
                />
                {watch('selectedPointRulesType') !== 'product' ? (
                  <Controller
                    key="amountBased"
                    control={control}
                    name="amountBasedPointRules"
                    rules={{
                      validate: (value) => {
                        const pointRuleType = watch('selectedPointRulesType');
                        if (
                          pointRuleType === 'allAmountUniform' ||
                          pointRuleType === 'moneyAmountUniform'
                        ) {
                          return !value.uniformAmountRule.amount
                            ? 'ポイント還元を設定してください'
                            : true;
                        }

                        if (
                          pointRuleType === 'allAmountCustom' ||
                          pointRuleType === 'moneyAmountCustom'
                        ) {
                          return value.customAmountRules.some((x) => !x.amount)
                            ? 'ポイント還元を設定してください'
                            : true;
                        }

                        return true;
                      },
                    }}
                    render={({ field: { value, onChange } }) => (
                      <AmountBasedPointRules
                        selectedPointRulesType={watch('selectedPointRulesType')}
                        uniformAmountRule={value.uniformAmountRule}
                        customAmountRules={value.customAmountRules}
                        onUniformAmountRuleChange={(rule) =>
                          onChange({
                            ...value,
                            uniformAmountRule: rule,
                          })
                        }
                        onCustomRulesChange={(rules) =>
                          onChange({
                            ...value,
                            customAmountRules: rules,
                          })
                        }
                        disabled={disabled}
                      />
                    )}
                  />
                ) : (
                  <Controller
                    key="productBased"
                    control={control}
                    name="productBasedPointRules"
                    rules={{
                      validate: (value) => {
                        if (watch('selectedPointRulesType') !== 'product') {
                          return true;
                        }

                        return (value.productBasedPointRules?.length ?? 0) < 1
                          ? 'ポイント還元を設定してください'
                          : true;
                      },
                    }}
                    render={({ field: { value, onChange } }) => (
                      <ProductBasedPointRules
                        miniumNumberForCombinationPurchase={
                          value.miniumNumberForCombinationPurchase
                        }
                        productBasedPointRules={value.productBasedPointRules}
                        onMiniumNumberForCombinationPurchaseChange={(v) =>
                          onChange({ ...value, miniumNumberForCombinationPurchase: v })
                        }
                        onProductBasedPointRulesChange={(rules) =>
                          onChange({ ...value, productBasedPointRules: rules })
                        }
                        disabled={disabled}
                      />
                    )}
                  />
                )}
              </Grid.Item>
            )}
          </Grid.Item>
          <Grid.Item size={6} startColumn={3}>
            <FormErrorMessage name="amountBasedPointRules" errors={errors} />
            <FormErrorMessage name="productBasedPointRules" errors={errors} />
          </Grid.Item>

          <Grid.Divider />

          <Grid.Item size={2}>
            <Label>ポイント還元の上限金額</Label>
          </Grid.Item>
          <Grid.Item size={3} flexDirection="column">
            <Grid.Item flexDirection="column">
              <Label>1ユーザーあたりの累計額</Label>
              <Controller
                control={control}
                name="maxTotalPointAmount"
                rules={{
                  validate: (v) => {
                    if (v) {
                      const maybeMaxTotalPointAmount = Number(v) || 0;

                      if (maybeMaxTotalPointAmount === 0) {
                        return '1以上の数字を入力してください';
                      }
                    }

                    return true;
                  },
                }}
                render={({ field: { value, onChange } }) => (
                  <LocalizedMask
                    value={value?.toString() ?? ''}
                    onChange={(e) => {
                      const numberPattern = /^[0-9]*$/;

                      if (numberPattern.test(e.target.value)) {
                        onChange(e.target.value);
                      }
                    }}
                  >
                    {({ value, ...inputProps }) => (
                      <TextBox
                        syncValue={value}
                        {...inputProps}
                        maxLength={8}
                        disabled={disabled}
                        fullWidth
                      />
                    )}
                  </LocalizedMask>
                )}
              />

              <FormErrorMessage name="maxTotalPointAmount" errors={errors} />
            </Grid.Item>

            <Grid.Item flexDirection="column">
              <LabelWithTooltipWrapper>
                <Label>1取引あたりの金額</Label>
                <Tooltip
                  id="max-point-amount"
                  text="ポイント還元を還元率に設定した時に1取引あたりの還元金額の上限値を設定します"
                />
              </LabelWithTooltipWrapper>

              <Controller
                control={control}
                name="maxPointAmount"
                rules={{
                  validate: (v) => {
                    if (v) {
                      const maybeMaxPointAmount = Number(v) || 0;

                      if (maybeMaxPointAmount === 0) {
                        return '1以上の数字を入力してください';
                      }
                    }

                    return true;
                  },
                }}
                render={({ field: { value, onChange } }) => (
                  <LocalizedMask
                    value={value?.toString() ?? ''}
                    onChange={(e) => {
                      const numberPattern = /^[0-9]*$/;

                      if (numberPattern.test(e.target.value)) {
                        onChange(e.target.value);
                      }
                    }}
                  >
                    {({ value, ...inputProps }) => (
                      <TextBox
                        syncValue={value}
                        {...inputProps}
                        maxLength={8}
                        disabled={disabled}
                        fullWidth
                      />
                    )}
                  </LocalizedMask>
                )}
              />

              <FormErrorMessage name="maxPointAmount" errors={errors} />
            </Grid.Item>

            <Grid.Item flexDirection="column">
              <Label style={{ marginTop: '0.5rem' }}>1キャンペーンあたりの金額</Label>
              <Controller
                control={control}
                name="budgetCapsAmount"
                rules={{
                  validate: (v) => {
                    if (v) {
                      const maybeCampaignBudgetCap = Number(v) || 0;

                      if (maybeCampaignBudgetCap === 0) {
                        return '1以上の数字を入力してください';
                      }

                      if (maybeCampaignBudgetCap > 10000000000) {
                        return 'キャンペーン上限設定は、10,000,000,000以内を設定してください';
                      }
                    }

                    return true;
                  },
                }}
                render={({ field: { value, onChange } }) => (
                  <LocalizedMask
                    value={value?.toString() ?? ''}
                    onChange={(e) => {
                      const numberPattern = /^[0-9]*$/;

                      if (numberPattern.test(e.target.value)) {
                        onChange(e.target.value);
                      }
                    }}
                  >
                    {({ value, ...inputProps }) => (
                      <TextBox
                        syncValue={value}
                        {...inputProps}
                        maxLength={11}
                        disabled={disabled}
                        fullWidth
                      />
                    )}
                  </LocalizedMask>
                )}
              />

              <FormErrorMessage name="budgetCapsAmount" errors={errors} />
            </Grid.Item>
          </Grid.Item>
          <Grid.Divider />
          <Grid.Item size={2}>
            <Label>ポイント有効期限</Label>
          </Grid.Item>
          <Grid.Item size={6} container gridSize={2} columnGap={24}>
            <Grid.Item flexDirection="column">
              <Label>日時指定</Label>
              <Controller
                control={control}
                name="pointExpiresAt"
                render={({ field: { value, onChange } }) => (
                  <>
                    <DateTime
                      value={value ?? undefined}
                      dateFormat="YYYY/MM/DD HH:mm:ss"
                      timeFormat={false}
                      onChange={(date) => onChange(date !== null ? date.endOf('day') : null)}
                      isValidDate={(current) =>
                        moment(current).isSameOrAfter(moment().startOf('day'))
                      }
                      showIcon
                    />
                    {/* The field renders a time it cannot edit, so say what a re-pick does to it */}
                    {willRepickChangePointExpiresAt(value) && (
                      <span style={{ color: '#757575', marginTop: '0.25rem' }}>
                        この有効期限はAPIで時刻付きで登録されています。日付を選び直すと選択日の23:59:59までに変わります
                      </span>
                    )}
                  </>
                )}
              />
            </Grid.Item>
            <Grid.Item flexDirection="column">
              <Label>相対日数指定</Label>
              <TextBox
                fullWidth
                {...register('pointExpiresInDays', { pattern: /\d+/, min: 1 })}
                pattern="\d+"
                placeholder="なし"
                type="number"
                min="1"
              />
              <span style={{ color: '#757575', marginTop: '0.25rem' }}>
                1にすると当日の24時までになります
              </span>
            </Grid.Item>
          </Grid.Item>
          <Grid.Divider />
          <Grid.Item size={2}>
            <Button>{mode === 'create' ? '登録' : '更新'}</Button>
          </Grid.Item>

          <Grid.Spacer />

          <Grid.Item size={12} nextRow>
            {isNullOrUndifined(inputErrMessage) ? null : (
              <ErrorMessage>{inputErrMessage}</ErrorMessage>
            )}
            <ApiResults messages={messages} />
          </Grid.Item>
        </FormGrid>
      </form>
    </ThemeProvider>
  );
};

export default EditCampaign;
